import {
	backofficeAuthDisableAdmin,
	backofficeAuthEnableAdmin,
	backofficeAuthResendInvite,
	type ResidenceManager,
	residenceRemoveManager
} from '@repo/sdk/gen'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@repo/ui/alert-dialog'
import { Badge, type BadgeVariant } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { DetailSection } from '@repo/ui/components/detail-section'
import { formatLocaleDate } from '@repo/ui/dates'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import { Skeleton } from '@repo/ui/skeleton'
import { Spinner } from '@repo/ui/spinner'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@repo/ui/table'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	Mail,
	MoreHorizontal,
	UserCheck,
	UserMinus,
	Users,
	UserX
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { type TKey, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { residenceManagersQueryOptions } from '../../-query/query-options'
import { AddManagerDialog } from './add-manager-dialog'

/** The facility manager accounts of a residence, with their commands. */
function ManagersSection({ residenceId }: { residenceId: string }) {
	const { t } = useTranslation()
	const query = useQuery(residenceManagersQueryOptions(residenceId))

	return (
		<DetailSection
			action={<AddManagerDialog residenceId={residenceId} />}
			icon={Users}
			title={t('admin-residences:managers.title')}
		>
			{query.isLoading && <Skeleton className='mx-4 h-24' />}
			{query.isError && (
				<p className='px-4 text-muted-foreground text-sm'>
					{t('admin-residences:managers.error')}
				</p>
			)}
			{query.data?.data.length === 0 && (
				<p className='px-4 text-muted-foreground text-sm'>
					{t('admin-residences:managers.empty')}
				</p>
			)}
			{query.data && query.data.data.length > 0 && (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>
								{t('admin-residences:managers.name')}
							</TableHead>
							<TableHead>
								{t('admin-residences:managers.email')}
							</TableHead>
							<TableHead>
								{t('admin-residences:managers.status')}
							</TableHead>
							<TableHead>
								{t('admin-residences:managers.invited_at')}
							</TableHead>
							<TableHead>
								{t('admin-residences:managers.last_sign_in')}
							</TableHead>
							<TableHead className='sr-only'>
								{t('admin-residences:managers.actions')}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{query.data.data.map((manager) => (
							<ManagerRow
								key={manager.id}
								manager={manager}
								residenceId={residenceId}
							/>
						))}
					</TableBody>
				</Table>
			)}
		</DetailSection>
	)
}

function ManagerRow({
	manager,
	residenceId
}: {
	manager: ResidenceManager
	residenceId: string
}) {
	const { t } = useTranslation()
	// One account can manage several residences (decision D17).
	const others = manager.residenceIds.filter(
		(id) => id !== residenceId
	).length

	return (
		<TableRow>
			<TableCell>
				<span className='font-medium'>{manager.name}</span>
				{others > 0 && (
					<span className='block text-muted-foreground text-xs'>
						{t('admin-residences:managers.other_residences', {
							count: others
						})}
					</span>
				)}
			</TableCell>
			<TableCell>{manager.email}</TableCell>
			<TableCell>
				<Badge variant={STATUS_TONE[manager.status]}>
					{t(STATUS_LABEL[manager.status])}
				</Badge>
			</TableCell>
			<TableCell className='tabular-nums'>
				{formatLocaleDate(manager.invitedAt, 'P')}
			</TableCell>
			<TableCell className='tabular-nums'>
				{manager.lastSignInAt
					? formatLocaleDate(manager.lastSignInAt, 'P HH:mm')
					: t('admin-residences:managers.never')}
			</TableCell>
			<TableCell className='w-12 text-right'>
				<ManagerActions
					manager={manager}
					others={others}
					residenceId={residenceId}
				/>
			</TableCell>
		</TableRow>
	)
}

function ManagerActions({
	manager,
	residenceId,
	others
}: {
	manager: ResidenceManager
	residenceId: string
	others: number
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const [confirmDisable, setConfirmDisable] = useState(false)
	const name = manager.name ?? manager.email ?? ''

	// The account shows on the managers list of each of its residences.
	const refresh = () =>
		Promise.all(
			manager.residenceIds.map((id) =>
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.residences.managers(id)
				})
			)
		)

	const resend = useMutation({
		mutationFn: () =>
			backofficeAuthResendInvite({ body: { adminId: manager.id } }),
		onSuccess: () => {
			toast.success(t('admin-residences:managers.invite_resent'))
		},
		onError: (error) => toast.error(formatError(error))
	})
	const remove = useMutation({
		mutationFn: () =>
			residenceRemoveManager({
				body: { data: { residenceId, managerId: manager.id } }
			}),
		onSuccess: async () => {
			await refresh()
			toast.success(t('admin-residences:managers.removed', { name }))
		},
		onError: (error) => toast.error(formatError(error))
	})
	const disable = useMutation({
		mutationFn: () =>
			backofficeAuthDisableAdmin({ body: { data: { id: manager.id } } }),
		onSuccess: async () => {
			await refresh()
			setConfirmDisable(false)
			toast.success(t('admin-residences:managers.disabled'))
		},
		onError: (error) => toast.error(formatError(error))
	})
	const enable = useMutation({
		mutationFn: () =>
			backofficeAuthEnableAdmin({ body: { data: { id: manager.id } } }),
		onSuccess: async () => {
			await refresh()
			toast.success(t('admin-residences:managers.enabled'))
		},
		onError: (error) => toast.error(formatError(error))
	})

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger
					render={
						<Button size='icon' variant='ghost'>
							<MoreHorizontal />
							<span className='sr-only'>
								{t('admin-residences:managers.open_menu', {
									name
								})}
							</span>
						</Button>
					}
				/>
				<DropdownMenuContent align='end' className='w-60'>
					{manager.status === 'pending' && (
						<DropdownMenuItem
							disabled={resend.isPending}
							onClick={() => resend.mutate()}
						>
							<Mail />
							{t('admin-residences:managers.resend_invite')}
						</DropdownMenuItem>
					)}
					{/* The last residence goes with the account: disable it instead. */}
					{others > 0 && (
						<DropdownMenuItem
							disabled={remove.isPending}
							onClick={() => remove.mutate()}
						>
							<UserMinus />
							{t('admin-residences:managers.remove')}
						</DropdownMenuItem>
					)}
					{(manager.status === 'pending' || others > 0) && (
						<DropdownMenuSeparator />
					)}
					{manager.status === 'disabled' ? (
						<DropdownMenuItem
							disabled={enable.isPending}
							onClick={() => enable.mutate()}
						>
							<UserCheck />
							{t('admin-residences:managers.enable')}
						</DropdownMenuItem>
					) : (
						<DropdownMenuItem
							disabled={disable.isPending}
							onClick={() => {
								// Only an account on other residences too asks first.
								if (others > 0) {
									setConfirmDisable(true)
								} else {
									disable.mutate()
								}
							}}
							variant='destructive'
						>
							<UserX />
							{t('admin-residences:managers.disable')}
						</DropdownMenuItem>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
			<AlertDialog onOpenChange={setConfirmDisable} open={confirmDisable}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('admin-residences:managers.disable_all_title', {
								name
							})}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t(
								'admin-residences:managers.disable_all_description',
								{ count: others + 1 }
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={disable.isPending}>
							{t('common:cancel')}
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={disable.isPending}
							onClick={() => disable.mutate()}
							variant='destructive'
						>
							{disable.isPending && <Spinner />}
							{t('admin-residences:managers.disable')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}

const STATUS_LABEL: Record<ResidenceManager['status'], TKey> = {
	pending: 'admin-residences:managers.statuses.pending',
	active: 'admin-residences:managers.statuses.active',
	disabled: 'admin-residences:managers.statuses.disabled'
}

const STATUS_TONE: Record<ResidenceManager['status'], BadgeVariant> = {
	pending: 'warning',
	active: 'success',
	disabled: 'destructive'
}

export { ManagersSection }
